import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import firebase from '../firebaseConfig';
import { InboxKeys, GlobalLinkKeys } from '../rtdbKeys';
import { resolveArtURL } from '../signedURLCache';
import { useAlbumArtURL } from '../hooks/useAlbumArtURL';
import GradientThumb from './GradientThumb';
import EditableName from './EditableName';
import FolderDropModal from './FolderDropModal';
import brandIcon from '../icon.png';
import { plural } from '../utils';
import { extractFolderDrop, importGroupsAsProjects } from '../folderDrop';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronDown,
    faMagnifyingGlass,
    faCubes,
    faUserGroup,
    faPlus,
    faGear,
    faLock,
    faUser,
    faFolder,
    faMusic,
    faAlignLeft,
    faMicrophone,
} from '@fortawesome/free-solid-svg-icons';

const CATEGORY_META = {
    artist:  { icon: faUser,        label: 'Artist'  },
    project: { icon: faFolder,      label: 'Project' },
    track:   { icon: faMusic,       label: 'Track'   },
    lyrics:  { icon: faAlignLeft,   label: 'Lyrics'  },
    idea:    { icon: faMicrophone,  label: 'Idea'    },
};

function ProjectRow({ project, isActive, onSelect, onRename }) {
    const imageUrl = useAlbumArtURL(project);
    return (
        <div
            className={`left-project-row ${isActive ? 'active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(project)}
            onKeyDown={(e) => {
                // Ignore keys from the inline rename editor — otherwise
                // Space/Enter get hijacked as row-select.
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(project);
                }
            }}
        >
            <GradientThumb id={project.id} imageUrl={imageUrl} className="left-project-thumb" />
            <EditableName
                value={project.name}
                placeholder="Untitled"
                className="left-project-name"
                title="Double-click to rename"
                activateOn="doubleClick"
                onSave={(name) => onRename?.(project, name)}
            />
            <span className="left-project-count">{project.numberOfTracks ?? ''}</span>
        </div>
    );
}

const NAV = [
    { id: 'all', icon: faCubes, label: 'All Projects' },
    { id: 'shared', icon: faUserGroup, label: 'Shared with me' },
];

const LeftPanel = ({
    selectedArtist,
    onPickArtist,
    projects,
    selectedProject,
    onSelectProject,
    onSearchSelect,
    onCreateProject,
    onProjectsCreated,
    onRenameProject,
    onOpenShared,
    onOpenSharedToken,
    onSettings,
}) => {
    const [navActive, setNavActive] = useState('all');
    const [folderDrop, setFolderDrop] = useState(null);
    const [projectsDragOver, setProjectsDragOver] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (selectedProject?.isShared) setNavActive('shared');
    }, [selectedProject?.id, selectedProject?.isShared]);
    const [query, setQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [sharedItems, setSharedItems] = useState([]);
    const [sharedLoading, setSharedLoading] = useState(false);
    const [sharedNames, setSharedNames] = useState({});
    const [sharedLocks, setSharedLocks] = useState({});
    const [sharedImages, setSharedImages] = useState({});
    const tokenRef = useRef(0);
    const searchInputRef = useRef(null);
    const dragEndTimer = useRef(null);

    useEffect(() => () => clearTimeout(dragEndTimer.current), []);

    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    useEffect(() => {
        const trimmed = query.trim();
        if (!trimmed) {
            setSearchResults([]);
            setSearching(false);
            return;
        }
        setSearching(true);
        const myToken = ++tokenRef.current;
        const handle = setTimeout(async () => {
            try {
                const search = firebase.functions().httpsCallable('search');
                const response = await search({ query: trimmed });
                if (myToken !== tokenRef.current) return;
                setSearchResults(response?.data?.results || []);
            } catch {
                if (myToken !== tokenRef.current) return;
                setSearchResults([]);
            } finally {
                if (myToken === tokenRef.current) setSearching(false);
            }
        }, 250);
        return () => clearTimeout(handle);
    }, [query]);

    useEffect(() => {
        if (navActive !== 'shared') return;
        const uid = firebase.auth().currentUser?.uid;
        if (!uid) { setSharedItems([]); return; }
        let cancelled = false;
        setSharedLoading(true);
        const inboxRef = firebase.database().ref('users').child(uid).child(InboxKeys.openedShareLinks);
        inboxRef.once('value')
            .then((snap) => {
                if (cancelled) return;
                const val = snap.val() || {};
                const mapEntry = (kind) => ([id, raw]) => ({
                    key: `${kind === InboxKeys.global ? 'g' : 'i'}:${id}`,
                    kind,
                    slug: kind === InboxKeys.global ? id : null,
                    token: kind === InboxKeys.invite ? id : null,
                    artistId: raw[InboxKeys.artistId],
                    projectId: raw[InboxKeys.projectId],
                    trackId: raw[InboxKeys.trackId] || null,
                    targetType: raw[InboxKeys.targetType] || 'project',
                    permission: raw[InboxKeys.permission] || 'view',
                    lastOpenedAt: raw[InboxKeys.lastOpenedAt] || 0,
                });
                const all = [
                    ...Object.entries(val[InboxKeys.global] || {}).map(mapEntry(InboxKeys.global)),
                    ...Object.entries(val[InboxKeys.invite] || {}).map(mapEntry(InboxKeys.invite)),
                ].filter((it) => it.targetType === 'project' || it.targetType === 'track');
                // Same content can land via /p/, /s/, or multiple tokens —
                // keep the entry granting the most access (editor>view, then most-recent).
                const rank = (e) =>
                    (e.permission === 'editor' ? 1e15 : 0) + (e.lastOpenedAt || 0);
                const byContentId = new Map();
                for (const it of all) {
                    const contentId = it.targetType === 'track'
                        ? `t:${it.artistId}:${it.projectId}:${it.trackId}`
                        : `p:${it.artistId}:${it.projectId}`;
                    const existing = byContentId.get(contentId);
                    if (!existing || rank(it) > rank(existing)) {
                        byContentId.set(contentId, it);
                    }
                }
                const items = [...byContentId.values()]
                    .sort((a, b) => (b.lastOpenedAt || 0) - (a.lastOpenedAt || 0));
                setSharedItems(items);
            })
            .catch(() => { if (!cancelled) setSharedItems([]); })
            .finally(() => { if (!cancelled) setSharedLoading(false); });
        return () => { cancelled = true; };
    }, [navActive]);

    useEffect(() => {
        if (navActive !== 'shared' || sharedItems.length === 0) return;
        let cancelled = false;
        sharedItems.forEach(async (item) => {
            if (sharedNames[item.key] || !item.artistId || !item.projectId) return;
            const isTrack = item.targetType === 'track' && item.trackId;
            try {
                const projectRef = firebase.database()
                    .ref('artists').child(item.artistId)
                    .child('projects').child(item.projectId);
                const isGlobal = item.kind === InboxKeys.global;
                const trackOrProjectRef = isTrack
                    ? projectRef.child('tracks').child(item.trackId)
                    : projectRef;
                const settingsRef = isGlobal
                    ? trackOrProjectRef.child('links').child('global').child(GlobalLinkKeys.settings)
                    : null;
                const nameRef = isTrack
                    ? projectRef.child('tracks').child(item.trackId).child('metadata').child('name')
                    : projectRef.child('metadata').child('name');
                const [projMetaSnap, nameSnap, settingsSnap] = await Promise.all([
                    projectRef.child('metadata').once('value'),
                    nameRef.once('value'),
                    settingsRef ? settingsRef.once('value') : Promise.resolve(null),
                ]);
                if (cancelled) return;
                const projMeta = projMetaSnap.val() || {};
                const name = nameSnap.val();
                const settings = (settingsSnap && settingsSnap.val()) || {};
                setSharedNames((prev) => ({
                    ...prev,
                    [item.key]: name || (isTrack ? 'Untitled track' : 'Untitled'),
                }));
                if (settings[GlobalLinkKeys.passwordHash]) {
                    setSharedLocks((prev) => ({ ...prev, [item.key]: true }));
                }
                const albumArtId = projMeta['album art'];
                if (albumArtId) {
                    let url = null;
                    try {
                        url = await resolveArtURL({
                            artistId: item.artistId,
                            projectId: item.projectId,
                            artId: albumArtId,
                        });
                    } catch {
                        url = null;
                    }
                    if (cancelled) return;
                    if (url) setSharedImages((prev) => ({ ...prev, [item.key]: url }));
                }
            } catch {
                if (cancelled) return;
                setSharedNames((prev) => ({
                    ...prev,
                    [item.key]: isTrack ? 'Shared track' : 'Shared project',
                }));
            }
        });
        return () => { cancelled = true; };
    }, [sharedItems, navActive]); // eslint-disable-line react-hooks/exhaustive-deps

    const initials = selectedArtist?.getInitials?.() || '··';
    const showResults = query.trim().length > 0;

    const handleProjectsDragOver = (e) => {
        if (!selectedArtist || !e.dataTransfer?.types?.includes('Files')) return;
        e.preventDefault();
        if (!projectsDragOver) setProjectsDragOver(true);
        // `dragover` fires continuously while a folder is over the pane,
        // including across child elements. Treat a gap in those events as
        // the exit — `dragleave`'s relatedTarget is unreliable at element
        // boundaries and made the drop state oscillate (visual stutter).
        if (dragEndTimer.current) clearTimeout(dragEndTimer.current);
        dragEndTimer.current = setTimeout(() => setProjectsDragOver(false), 250);
    };

    const createProjectsFromGroups = (groups) =>
        importGroupsAsProjects(selectedArtist, groups, onProjectsCreated);

    const handleProjectsDrop = async (e) => {
        e.preventDefault();
        if (dragEndTimer.current) clearTimeout(dragEndTimer.current);
        setProjectsDragOver(false);
        if (!selectedArtist) return;
        // The projects list only makes new projects — loose files have no home here.
        const drop = await extractFolderDrop(e.dataTransfer);
        if (!drop.hasFolders) return;
        // Nested folders are ambiguous (one project, or one per sub-folder?) —
        // ask. Plain sibling folders just become a project each.
        if (drop.isNested) {
            setFolderDrop(drop);
        } else {
            createProjectsFromGroups(drop.folderGroups);
        }
    };

    const handleFolderSeparate = (groups) => {
        setFolderDrop(null);
        createProjectsFromGroups(groups);
    };

    const handleFolderConsolidate = () => {
        const drop = folderDrop;
        setFolderDrop(null);
        createProjectsFromGroups(drop.topGroups);
    };

    return (
        <aside className="left-panel">
            <div className="left-brand" aria-hidden="true">
                <img src={brandIcon} alt="" className="left-brand-logo" />
                <span className="left-brand-name">demodeck</span>
            </div>
            <button className="workspace-card" onClick={onPickArtist}>
                <div className="workspace-avatar">{initials}</div>
                <div className="workspace-text">
                    <div className="workspace-name">{selectedArtist?.name || 'No artist'}</div>
                    <div className="workspace-sub">Workspace</div>
                </div>
                <FontAwesomeIcon icon={faChevronDown} className="workspace-chev" />
            </button>

            <div className="left-search">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="left-search-icon" />
                <input
                    ref={searchInputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search projects, tracks..."
                />
                <kbd className="kbd">⌘K</kbd>
            </div>

            {showResults ? (
                <div className="search-results-list">
                    {searching && <div className="left-empty">Searching…</div>}
                    {!searching && searchResults.length === 0 && (
                        <div className="left-empty">No results.</div>
                    )}
                    {!searching && searchResults.map((hit, i) => (
                        <button
                            key={`${hit.category}-${i}`}
                            className="left-search-row"
                            onClick={() => { onSearchSelect(hit); setQuery(''); }}
                        >
                            <div className="left-search-row-title">
                                {hit.title}
                                {(() => {
                                    const meta = CATEGORY_META[hit.category];
                                    if (!meta) return null;
                                    return (
                                        <span className="left-search-row-tag">
                                            <FontAwesomeIcon icon={meta.icon} /> {meta.label}
                                        </span>
                                    );
                                })()}
                                {hit.isShared && (
                                    <span className="left-search-row-tag left-search-row-tag-shared">
                                        <FontAwesomeIcon icon={faUserGroup} /> Shared with Me
                                    </span>
                                )}
                            </div>
                            <div className="left-search-row-info">{hit.info}</div>
                        </button>
                    ))}
                </div>
            ) : (
                <>
                    <nav className="left-nav">
                        {NAV.map((n) => (
                            <button
                                key={n.id}
                                className={`left-nav-row ${navActive === n.id ? 'active' : ''}`}
                                onClick={() => setNavActive(n.id)}
                            >
                                <FontAwesomeIcon icon={n.icon} className="left-nav-icon" />
                                <span className="left-nav-label">{n.label}</span>
                                {n.id === 'all' && <span className="left-nav-count">{projects.length}</span>}
                                {n.id === 'shared' && sharedItems.length > 0 && (
                                    <span className="left-nav-count">{sharedItems.length}</span>
                                )}
                            </button>
                        ))}
                    </nav>

                    {navActive === 'shared' ? (
                        <>
                            <div className="left-section-header"><span>SHARED WITH ME</span></div>
                            <div className="left-projects">
                                {sharedLoading && sharedItems.length === 0 && (
                                    <div className="left-empty">Loading…</div>
                                )}
                                {!sharedLoading && sharedItems.length === 0 && (
                                    <div className="left-empty">Nothing here yet — open a share link.</div>
                                )}
                                {sharedItems.map((item) => {
                                    const isTrack = item.targetType === 'track';
                                    const isGlobal = item.kind === InboxKeys.global;
                                    const isActive = !isTrack
                                        && selectedProject?.isShared
                                        && (isGlobal
                                            ? selectedProject?.slug === item.slug
                                            : selectedProject?.token === item.token);
                                    const isLocked = sharedLocks[item.key];
                                    const onClick = () => {
                                        if (isTrack) {
                                            navigate(isGlobal ? `/t/${item.slug}` : `/s/${item.token}`);
                                            return;
                                        }
                                        const preview = {
                                            artistId: item.artistId,
                                            projectId: item.projectId,
                                            name: sharedNames[item.key],
                                            imageUrl: sharedImages[item.key],
                                        };
                                        if (isGlobal) {
                                            onOpenShared?.(item.slug, preview);
                                        } else {
                                            onOpenSharedToken?.(item.token, preview);
                                        }
                                    };
                                    return (
                                        <button
                                            key={item.key}
                                            className={`left-project-row ${isActive ? 'active' : ''}`}
                                            onClick={onClick}
                                        >
                                            <GradientThumb id={item.trackId || item.projectId} imageUrl={sharedImages[item.key]} className="left-project-thumb" />
                                            <span className="left-project-name">
                                                {isTrack && (
                                                    <FontAwesomeIcon icon={faMusic} className="left-project-kind" />
                                                )}
                                                {sharedNames[item.key] || 'Loading…'}
                                            </span>
                                            {isLocked && (
                                                <FontAwesomeIcon icon={faLock} className="left-project-lock" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="left-section-header">
                                <span>PROJECTS</span>
                                <button className="left-section-add" title="New project" onClick={onCreateProject}>
                                    <FontAwesomeIcon icon={faPlus} />
                                </button>
                            </div>

                            <div
                                className={`left-projects ${projectsDragOver ? 'drag-over' : ''}`}
                                onDragOver={handleProjectsDragOver}
                                onDrop={handleProjectsDrop}
                            >
                                {projects.length === 0 && !projectsDragOver && (
                                    <div className="left-empty">No projects yet.</div>
                                )}
                                {projects.map((p) => (
                                    <ProjectRow
                                        key={p.id}
                                        project={p}
                                        isActive={selectedProject?.id === p.id}
                                        onSelect={onSelectProject}
                                        onRename={onRenameProject}
                                    />
                                ))}
                                {/* Class swap, not a two-element ternary — avoids a remount per drag toggle. */}
                                <div className={projectsDragOver ? 'left-projects-drop-hint' : 'left-projects-hint'}>
                                    <FontAwesomeIcon icon={faFolder} />
                                    <span>Drop folders to add projects</span>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}

            <button className="left-settings" onClick={onSettings}>
                <FontAwesomeIcon icon={faGear} className="left-settings-icon" />
                <span>Settings</span>
            </button>

            {folderDrop && (
                <FolderDropModal
                    separateGroups={folderDrop.folderGroups}
                    combined={{
                        title: 'Combined project',
                        description: 'One project per dropped folder',
                        action: `Create ${plural(folderDrop.topGroups.length, 'project')}`,
                        rows: folderDrop.topGroups.map((g) => ({
                            name: g.name,
                            trackCount: g.files.length,
                        })),
                    }}
                    onSeparate={handleFolderSeparate}
                    onCombined={handleFolderConsolidate}
                    onCancel={() => setFolderDrop(null)}
                />
            )}
        </aside>
    );
};

export default LeftPanel;
